// ESC/POS command builder for 58mm thermal printers (Xprinter XP-58IIH and compatibles).
// Font A on 58mm prints up to 32 characters per line.

const ENCODER = new TextEncoder();
const LINE_WIDTH = 32;

// Control bytes
const ESC = 0x1b;
const GS = 0x1d;

const cmd = (...bytes) => new Uint8Array(bytes);

// Concatenate multiple Uint8Arrays
const concat = (chunks) => {
  const total = chunks.reduce((acc, c) => acc + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
};

// Strip diacritics so the printer (PC437/PC850) renders Spanish text legibly
// (we send a single-byte encoding fallback by removing accents).
const stripAccents = (s) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[áÁ]/g, (c) => (c === 'á' ? 'a' : 'A'))
    .replace(/[éÉ]/g, (c) => (c === 'é' ? 'e' : 'E'))
    .replace(/[íÍ]/g, (c) => (c === 'í' ? 'i' : 'I'))
    .replace(/[óÓ]/g, (c) => (c === 'ó' ? 'o' : 'O'))
    .replace(/[úÚ]/g, (c) => (c === 'ú' ? 'u' : 'U'))
    .replace(/[ñ]/g, 'n')
    .replace(/[Ñ]/g, 'N');

const text = (s) => ENCODER.encode(stripAccents(s));

// Format a 2-column row: left-aligned label, right-aligned value, padded to LINE_WIDTH
const row = (left, right, width = LINE_WIDTH) => {
  const l = stripAccents(String(left ?? ''));
  const r = stripAccents(String(right ?? ''));
  if (l.length + r.length + 1 > width) {
    // Wrap: print left line, then right-aligned on next line
    const lines = [];
    let remaining = l;
    while (remaining.length > width) {
      lines.push(remaining.slice(0, width));
      remaining = remaining.slice(width);
    }
    lines.push(remaining);
    const last = lines[lines.length - 1];
    const space = Math.max(1, width - last.length - r.length);
    lines[lines.length - 1] = last + ' '.repeat(space) + r;
    return lines.join('\n') + '\n';
  }
  const space = width - l.length - r.length;
  return l + ' '.repeat(space) + r + '\n';
};

const dashLine = (width = LINE_WIDTH) => '-'.repeat(width) + '\n';

// Payment method labels
const PAYMENT_LABELS = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  mixed: 'Mixto',
};

const fmtMoney = (n) =>
  '$' +
  Number(n || 0).toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (iso) => {
  try {
    const d = iso ? new Date(iso) : new Date();
    return d.toLocaleString('es-MX', {
      timeZone: 'America/Mexico_City',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Build the raw ESC/POS byte buffer for a sale receipt.
 * @param {object} opts
 * @param {object} opts.business - { name, address, phone }
 * @param {object} opts.sale - the sale object from the API
 * @param {string} [opts.clientName]
 */
export function buildSaleReceipt({ business, sale, clientName }) {
  const chunks = [];

  // Initialize, set CP437 (USA standard) and CP858 (with Euro) for $ symbol
  chunks.push(cmd(ESC, 0x40)); // ESC @ - initialize
  chunks.push(cmd(ESC, 0x74, 0x13)); // ESC t 19 -> PC858 (with €)

  // Header: centered, double-size title
  chunks.push(cmd(ESC, 0x61, 0x01)); // ESC a 1 - center
  chunks.push(cmd(GS, 0x21, 0x11)); // GS ! 0x11 - 2x width + 2x height
  chunks.push(text(business?.name || 'Ticket'));
  chunks.push(text('\n'));
  chunks.push(cmd(GS, 0x21, 0x00)); // normal size

  if (business?.address) {
    chunks.push(text(business.address + '\n'));
  }
  if (business?.phone) {
    chunks.push(text('Tel: ' + business.phone + '\n'));
  }
  chunks.push(text('\n'));

  // Ticket number + date (left aligned)
  chunks.push(cmd(ESC, 0x61, 0x00)); // left
  chunks.push(text(row('Ticket:', '#' + (sale?.ticket_number || sale?.id?.slice(-6) || '-'))));
  chunks.push(text(row('Fecha:', fmtDate(sale?.created_at))));
  if (clientName) {
    chunks.push(text(row('Cliente:', clientName.slice(0, 22))));
  }
  chunks.push(text(dashLine()));

  // Items
  for (const item of sale?.items || []) {
    const qty = item.quantity || 1;
    const unit = item.unit_price ?? item.price ?? 0;
    const subtotal = item.subtotal ?? unit * qty;
    chunks.push(text(`${qty} x ${stripAccents(item.name || '').slice(0, 26)}\n`));
    chunks.push(text(row(`  ${fmtMoney(unit)} c/u`, fmtMoney(subtotal))));
  }
  chunks.push(text(dashLine()));

  // Totals
  const subtotal = sale?.subtotal ?? sale?.total ?? 0;
  chunks.push(text(row('Subtotal:', fmtMoney(subtotal))));
  if (sale?.discount > 0) {
    chunks.push(text(row('Descuento:', '-' + fmtMoney(sale.discount))));
  }
  if (sale?.tip_amount > 0) {
    chunks.push(text(row('Propina:', fmtMoney(sale.tip_amount))));
  }

  chunks.push(cmd(GS, 0x21, 0x01)); // 2x height
  chunks.push(text(row('TOTAL:', fmtMoney(sale?.total ?? 0), 16)));
  chunks.push(cmd(GS, 0x21, 0x00));
  chunks.push(text('\n'));

  // Payment
  const payLabel = PAYMENT_LABELS[sale?.payment_method] || sale?.payment_method || '';
  chunks.push(text(row('Pago:', payLabel)));
  if (sale?.payment_method === 'mixed' && sale?.payment_details) {
    for (const [k, v] of Object.entries(sale.payment_details)) {
      if (Number(v) > 0) {
        chunks.push(text(row('  ' + (PAYMENT_LABELS[k] || k), fmtMoney(v))));
      }
    }
  }

  if (sale?.loyalty_points_earned > 0) {
    chunks.push(text('\n'));
    chunks.push(cmd(ESC, 0x61, 0x01));
    chunks.push(text(`+${sale.loyalty_points_earned} pts lealtad\n`));
    chunks.push(cmd(ESC, 0x61, 0x00));
  }

  // Footer
  chunks.push(text('\n'));
  chunks.push(cmd(ESC, 0x61, 0x01));
  chunks.push(text('Gracias por su preferencia\n'));
  chunks.push(text('\n\n\n'));

  // Full cut
  chunks.push(cmd(GS, 0x56, 0x00)); // GS V 0 - full cut

  return concat(chunks);
}

/**
 * Build a test print buffer.
 */
export function buildTestPrint(businessName = 'Barrabas POS') {
  const chunks = [];
  chunks.push(cmd(ESC, 0x40));
  chunks.push(cmd(ESC, 0x74, 0x13));
  chunks.push(cmd(ESC, 0x61, 0x01));
  chunks.push(cmd(GS, 0x21, 0x11));
  chunks.push(text(businessName + '\n'));
  chunks.push(cmd(GS, 0x21, 0x00));
  chunks.push(text('IMPRESION DE PRUEBA\n'));
  chunks.push(text(fmtDate() + '\n'));
  chunks.push(text(dashLine()));
  chunks.push(text('Si lees este texto la\n'));
  chunks.push(text('impresora esta lista.\n'));
  chunks.push(text(dashLine()));
  chunks.push(text('\n\n\n'));
  chunks.push(cmd(GS, 0x56, 0x00));
  return concat(chunks);
}
